import type { DeepReadonly } from "@itandi/itandi-bb-ui";
import { Button } from "@itandi/itandi-bb-ui/Button";
import { InputCheckBox } from "@itandi/itandi-bb-ui/InputCheckBox";
import { ModalBody } from "@itandi/itandi-bb-ui/ModalBody";
import { ModalFooter } from "@itandi/itandi-bb-ui/ModalFooter";
import { ModalHeader } from "@itandi/itandi-bb-ui/ModalHeader";
import { Modalv2 } from "@itandi/itandi-bb-ui/Modalv2";
import { useNotification } from "@itandi/itandi-bb-ui/NotificationBarStore";
import React from "react";
import { useDeals } from "~/hooks/useDeals";
import { useIdQueryHandler } from "~/hooks/useIdQueryHandler";
import { getOriginDealVendor } from "~/models/DealVendorCompany";
import { isSuccess } from "~/utils/api/api_handler";
import { update } from "~/utils/api/deals/DealVendorCompany";
import Styles from "./DealOriginVendorClientChangeModal.module.scss";

type Props = DeepReadonly<{
	handleClose: () => void;
	isOpen: boolean;
}>;

/**
 * 管理会社がoriginのDealVendorCompanyを更新するためのModal
 *
 * @constructor
 * @for client
 */
export const DealOriginVendorClientChangeModal: React.FC<Props> = ({
	isOpen,
	handleClose
}: Props) => {
	const { notify } = useNotification();
	const id = useIdQueryHandler();
	const { data: deal, mutate: mutateDeal } = useDeals().show(id);
	const [checked, setChecked] = React.useState<boolean>(false);
	React.useEffect(() => {
		if (deal !== null) {
			setChecked(getOriginDealVendor(deal)?.showTenant ?? false);
		}
	}, [deal]);

	const onChange = React.useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			setChecked(e.target.checked);
		},
		[]
	);

	const handleSubmit = React.useCallback(async () => {
		if (deal === null || id === null) {
			return;
		}
		const dealVendorCompanyId = getOriginDealVendor(deal)?.id;
		if (!dealVendorCompanyId) {
			return;
		}
		const response = await update(id, dealVendorCompanyId, {
			showTenant: checked
		});
		if (isSuccess(response)) {
			await mutateDeal?.();
			notify({ content: "更新しました。", variant: "success" });
			handleClose();
			return;
		}
		notify({ content: "エラーが発生しました。", variant: "error" });
	}, [checked, deal, handleClose, id, mutateDeal, notify]);

	if (deal === null) {
		return null;
	}
	return (
		<Modalv2 isOpen={isOpen} onClose={handleClose}>
			<ModalHeader title="案件依頼先情報変更" />
			<ModalBody>
				<div className={Styles.Forms}>
					<div className={Styles.FormRow}>
						<div className={Styles.FormLabelCol}>個人情報公開</div>
						<div className={Styles.FormSettingCol}>
							<InputCheckBox
								checked={checked}
								label="施工会社に個人情報を公開する"
								onChange={onChange}
							/>
						</div>
					</div>
				</div>
			</ModalBody>
			<ModalFooter
				leftArea={
					<Button onClick={handleClose} variant="secondary">
						保存せず閉じる
					</Button>
				}
				rightArea={
					<Button onClick={handleSubmit} variant="primary">
						保存
					</Button>
				}
			/>
		</Modalv2>
	);
};
